/**
 * OrgFairyChat Component
 *
 * DC-408: Organization-level FAIRy chat interface for the Governance section.
 * Provides AI-powered assistance for organizational context, governance
 * documents, interview analysis, and standards configuration.
 *
 * Features:
 * - Floating chat button with unread indicator
 * - Context-aware suggestions based on org data
 * - Quick actions for common governance tasks
 * - Persistent chat history
 *
 * @version 1.0.0
 * @date 2025-12-31
 */

import React, { useState, useEffect, useRef } from 'react';
import './org-fairy-chat.css';

// ============================================================================
// TYPES
// ============================================================================

interface ChatMessage {
  id: string;
  type: 'user' | 'fairy';
  text: string;
  timestamp: Date;
  suggestions?: string[];
  actions?: QuickAction[];
}

interface QuickAction {
  label: string;
  action: string;
  icon: string;
}

interface OrgContext {
  interview_count: number;
  document_count: number;
  standards_enabled: number;
  standards_total: number;
  execution_risk_level: string;
  recommendations_count: number;
}

interface OrgFairyChatProps {
  /** Optional callback for navigation actions */
  onNavigate?: (path: string) => void;
  /** Initial collapsed state */
  initialCollapsed?: boolean;
  /** Optional org context (fetched if not provided) */
  orgContext?: OrgContext;
}

// ============================================================================
// QUICK ACTIONS
// ============================================================================

const ORG_QUICK_ACTIONS: QuickAction[] = [
  { label: 'Analyze Interviews', action: 'analyze_interviews', icon: '📋' },
  { label: 'Review Documents', action: 'review_documents', icon: '📄' },
  { label: 'Check Standards', action: 'check_standards', icon: '📐' },
  { label: 'Execution Risk', action: 'execution_risk', icon: '⚠️' },
];

const PROACTIVE_PROMPTS: string[] = [
  "What is our organization's FAIR maturity level?",
  "How can we improve our data governance?",
  "What interoperability standards should we enable?",
  "Summarize our interview findings",
  "What are the top recommendations for our organization?",
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const OrgFairyChat: React.FC<OrgFairyChatProps> = ({
  initialCollapsed = true,
  orgContext: providedContext,
}) => {
  const [isOpen, setIsOpen] = useState(!initialCollapsed);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orgContext, setOrgContext] = useState<OrgContext | null>(providedContext || null);
  const [hasUnread, setHasUnread] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load org context on mount
  useEffect(() => {
    if (!providedContext) {
      fetchOrgContext();
    }
  }, [providedContext]);

  // Add welcome message when opened for first time
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addFairyMessage(getWelcomeMessage());
    }
    if (isOpen) {
      setHasUnread(false);
      inputRef.current?.focus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on isOpen change, functions are stable
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchOrgContext = async () => {
    try {
      const response = await fetch('/api/organization/execution-risk');
      if (response.ok) {
        const data = await response.json();
        setOrgContext({
          interview_count: data.interview_count || 0,
          document_count: data.document_count || 0,
          standards_enabled: data.standards_enabled || 0,
          standards_total: data.standards_total || 4,
          execution_risk_level: data.execution_risk?.risk_level || 'NOT_CONFIGURED',
          recommendations_count: 0, // TODO: fetch from recommendations API
        });
      }
    } catch (err) {
      console.error('[OrgFairyChat] Failed to fetch org context:', err);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getWelcomeMessage = (): string => {
    if (!orgContext) {
      return "👋 Hello! I'm FAIRy, your AI governance assistant. I can help you understand and improve your organization's FAIR maturity. What would you like to know?";
    }

    const { interview_count, document_count, execution_risk_level } = orgContext;

    if (interview_count === 0 && document_count === 0) {
      return "👋 Welcome! I notice you haven't set up any organizational context yet. I can help you:\n\n• Conduct stakeholder interviews\n• Upload governance documents\n• Configure interoperability standards\n\nWhere would you like to start?";
    }

    if (execution_risk_level === 'NOT_CONFIGURED') {
      return `👋 I see you have ${interview_count} interviews and ${document_count} documents. To calculate your Execution Risk score, we need a bit more data. Would you like me to help configure your organizational assessments?`;
    }

    return `👋 Welcome back! Your organization has ${interview_count} interviews, ${document_count} documents, and an Execution Risk level of ${execution_risk_level}. How can I help you today?`;
  };

  const addFairyMessage = (text: string, suggestions?: string[], actions?: QuickAction[]) => {
    const message: ChatMessage = {
      id: `fairy-${Date.now()}`,
      type: 'fairy',
      text,
      timestamp: new Date(),
      suggestions,
      actions,
    };
    setMessages((prev) => [...prev, message]);
    if (!isOpen) {
      setHasUnread(true);
    }
  };

  const addUserMessage = (text: string) => {
    const message: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, message]);
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    addUserMessage(userMessage);
    setIsLoading(true);

    try {
      const response = await fetch('/api/fairy/org-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: userMessage,
          context: orgContext,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        addFairyMessage(
          data.response || "I'm not sure how to answer that. Could you try rephrasing?",
          data.suggestions,
          data.actions
        );
      } else {
        // Fallback response when API is not available
        addFairyMessage(
          getLocalResponse(userMessage),
          PROACTIVE_PROMPTS.slice(0, 3)
        );
      }
    } catch (err) {
      console.error('[OrgFairyChat] Query failed:', err);
      addFairyMessage(
        getLocalResponse(userMessage),
        PROACTIVE_PROMPTS.slice(0, 3)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getLocalResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('interview')) {
      const count = orgContext?.interview_count || 0;
      return count > 0
        ? `You have ${count} interview(s) recorded. These provide valuable insights into your organization's data governance practices. Would you like me to summarize the key themes?`
        : "You haven't conducted any interviews yet. Stakeholder interviews help capture organizational context that affects FAIR maturity. Click on 'Interviews' in the navigation to start.";
    }

    if (lowerQuery.includes('document')) {
      const count = orgContext?.document_count || 0;
      return count > 0
        ? `You have ${count} governance document(s) uploaded. These inform our understanding of your data policies and practices.`
        : "No governance documents have been uploaded yet. Documents like data policies, standards guides, and SOPs help establish baseline governance maturity.";
    }

    if (lowerQuery.includes('standard') || lowerQuery.includes('interop')) {
      const { standards_enabled = 0, standards_total = 4 } = orgContext || {};
      return `You have ${standards_enabled} of ${standards_total} interoperability standards enabled. Enabling more standards helps ensure your data can work across systems and organizations.`;
    }

    if (lowerQuery.includes('risk') || lowerQuery.includes('execution')) {
      const level = orgContext?.execution_risk_level || 'NOT_CONFIGURED';
      return level === 'NOT_CONFIGURED'
        ? "Your Execution Risk hasn't been calculated yet. Complete organizational interviews and upload governance documents to enable this metric."
        : `Your current Execution Risk level is ${level}. This reflects your organization's readiness to execute FAIR initiatives based on people, governance, and interoperability factors.`;
    }

    if (lowerQuery.includes('recommend') || lowerQuery.includes('improve')) {
      return "Based on your organizational context, I recommend:\n\n1. Conduct stakeholder interviews to understand current practices\n2. Upload governance documentation for baseline assessment\n3. Enable relevant interoperability standards\n4. Review recommendations in the Recommendations tab\n\nWhich area would you like to focus on first?";
    }

    return "I can help you with governance configuration, interview analysis, document review, and FAIR maturity assessment. What specific aspect would you like to explore?";
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'analyze_interviews':
        addUserMessage('Analyze our interview findings');
        addFairyMessage(
          orgContext?.interview_count
            ? "I'll analyze your interview data to extract key themes and recommendations. This feature provides insights into stakeholder perspectives on data governance."
            : "You don't have any interviews recorded yet. Would you like to learn how to conduct effective FAIR-focused stakeholder interviews?",
          ['How to conduct interviews', 'Interview question templates']
        );
        break;
      case 'review_documents':
        addUserMessage('Review our governance documents');
        addFairyMessage(
          orgContext?.document_count
            ? "Your governance documents have been analyzed. Key themes include data quality standards, access policies, and metadata requirements."
            : "No documents uploaded yet. Governance documents help establish baseline maturity and identify gaps.",
          ['Upload documents', 'Document types to include']
        );
        break;
      case 'check_standards':
        addUserMessage('What standards should we enable?');
        addFairyMessage(
          "For life sciences organizations, I recommend enabling:\n\n• HGNC - Gene nomenclature\n• Gene Ontology - Biological processes\n• UniProt - Protein data\n• ChEBI - Chemical entities\n\nThese standards improve data interoperability and FAIR compliance.",
          ['Enable standards', 'Learn about each standard']
        );
        break;
      case 'execution_risk':
        addUserMessage('Explain our Execution Risk score');
        const level = orgContext?.execution_risk_level || 'NOT_CONFIGURED';
        addFairyMessage(
          level === 'NOT_CONFIGURED'
            ? "Execution Risk measures your organization's readiness to implement FAIR initiatives. It combines:\n\n• People Readiness (interviews)\n• Governance Maturity (documents)\n• Interoperability (standards)\n\nComplete these assessments to calculate your score."
            : `Your Execution Risk is ${level}. This is calculated from three factors:\n\n• People Readiness - Staff capabilities\n• Governance Maturity - Policy framework\n• Interoperability - Technical standards\n\nLower risk means better execution readiness.`
        );
        break;
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={`org-fairy-chat ${isOpen ? 'org-fairy-chat--open' : ''}`}>
      {/* Chat Toggle Button */}
      <button
        className="fairy-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close FAIRy chat' : 'Open FAIRy chat'}
        title="Ask FAIRy"
      >
        <span className="fairy-icon">🧚</span>
        {hasUnread && <span className="unread-indicator" />}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fairy-panel">
          {/* Header */}
          <div className="fairy-header">
            <div className="fairy-title">
              <span className="fairy-avatar">🧚</span>
              <div className="fairy-name">
                <strong>Ask FAIRy</strong>
                <span className="fairy-subtitle">Governance Assistant</span>
              </div>
            </div>
            <button
              className="fairy-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Quick Actions */}
          <div className="fairy-quick-actions">
            {ORG_QUICK_ACTIONS.map((action) => (
              <button
                key={action.action}
                className="quick-action-btn"
                onClick={() => handleQuickAction(action.action)}
              >
                <span className="action-icon">{action.icon}</span>
                <span className="action-label">{action.label}</span>
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="fairy-messages">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`fairy-message fairy-message--${msg.type}`}
              >
                {msg.type === 'fairy' && (
                  <span className="message-avatar">🧚</span>
                )}
                <div className="message-content">
                  <p className="message-text">{msg.text}</p>
                  {msg.suggestions && msg.suggestions.length > 0 && (
                    <div className="message-suggestions">
                      {msg.suggestions.map((suggestion, i) => (
                        <button
                          key={i}
                          className="suggestion-chip"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="fairy-message fairy-message--fairy fairy-typing">
                <span className="message-avatar">🧚</span>
                <div className="typing-indicator">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="fairy-input-area">
            <input
              ref={inputRef}
              type="text"
              className="fairy-input"
              placeholder="Ask about governance, interviews, standards..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <button
              className="fairy-send-btn"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgFairyChat;
